import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Paperclip, Plus, UploadCloud, X } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import employeeRequestService from "../../api/employeeRequestService";
import { apiErrorMessage } from "../../api/taskBoardService";
import { ErrorState } from "../task-board/shared";
import { STATUS_LABELS } from "../task-board/constants";
import {
  DELETED_COLOR, REQUESTS_CHANGED_EVENT, SEVERITY_COLOR, STATUSES, STATUS_COLOR, TYPE_META, severityLabel, statusLabel, typeLabel,
} from "./constants";
import { firstNameOf, fullDate, isArabic, pillStyle, refOf, tint } from "./format";
import AttachmentsSection from "./AttachmentsSection";
import PendingFiles from "./PendingFiles";
import Conversation from "./Conversation";
import EmployeeCard from "./EmployeeCard";
import SendEmailModal from "./SendEmailModal";

const escapeHtml = (v) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/** Task draft: title + rich-text description that credits the requester and date. */
const buildTaskDraft = (req) => {
  const e = req.employee;
  const who = [e.name, e.employeeCode && `(${e.employeeCode})`, e.department?.name && `– ${e.department.name}`].filter(Boolean).join(" ");
  const when = new Date(req.createdAt).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  const kind = typeLabel(req.type) + (req.severity ? ` · ${severityLabel(req.severity)} severity` : "");
  const body = String(req.description || "")
    .split(/\r?\n/)
    .map((line) => `<p>${escapeHtml(line) || "<br>"}</p>`)
    .join("");
  return {
    employeeRequest: req.id, // hidden link: the board saves it on the task
    title: req.title,
    description:
      `<p><strong>Requested by:</strong> ${escapeHtml(who)}</p>` +
      `<p><strong>Requested on:</strong> ${escapeHtml(when)}</p>` +
      `<p><strong>Type:</strong> ${escapeHtml(kind)}</p>` +
      `<p><br></p>${body}`,
  };
};

const CLOSE_MS = 260;
const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // mirrors the API limits

/**
 * Right-side details panel. Non-admins get a view-only panel (the API rejects
 * their actions anyway). Closing plays the mirrored slide-out, then unmounts.
 */
const RequestPanel = ({ requestId, onClosed }) => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const canAct = isAdmin(auth?.employee);
  const me = { id: auth?.employee?._id || auth?.employee?.id || auth?.employee?.employeeId, name: auth?.employee?.fullNameEn };

  const [req, setReq] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);
  const bodyRef = useRef(null);
  const composerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState("");

  // Composer is one line (same height as Send) and grows only as the reply gets longer.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "38px";
    el.style.height = `${Math.min(Math.max(el.scrollHeight, 38), 120)}px`;
  }, [draft, req]);

  // Hide the floating AI assistant while the panel (or the email modal) is open.
  useEffect(() => {
    document.body.classList.add("er-overlay-open");
    return () => document.body.classList.remove("er-overlay-open");
  }, []);

  const close = useCallback(() => {
    if (closing) return; // ignore repeat clicks while it animates out
    setClosing(true);
    closeTimer.current = setTimeout(onClosed, CLOSE_MS);
  }, [closing, onClosed]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    const onKey = (e) => {
      // Esc closes the topmost layer only: the lightbox / email modal handle their own.
      if (e.key === "Escape" && !document.querySelector(".er-lb, .er-modalwrap")) close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  // Load the request; opening it (or seeing it change live) counts as reading it.
  const fetchDetail = useCallback(async () => {
    try {
      const r = await employeeRequestService.get(requestId);
      setReq(r);
      if (canAct && r.hasUpdate) {
        await employeeRequestService.markRead(requestId).catch(() => {});
        window.dispatchEvent(new Event(REQUESTS_CHANGED_EVENT)); // refresh badge + list
      }
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load request"));
    }
  }, [requestId, canAct]);

  useEffect(() => {
    setReq(null);
    setError("");
    setDraft("");
    fetchDetail();
  }, [requestId, fetchDetail]);

  // Live: the employee replied (or another admin acted) on the request that is open.
  useEffect(() => {
    const onChange = (e) => {
      const d = e.detail;
      if (d && (d.action === "resync" || d.requestId === requestId)) fetchDetail();
    };
    window.addEventListener(REQUESTS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(REQUESTS_CHANGED_EVENT, onChange);
  }, [requestId, fetchDetail]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      setReq(await fn());
      window.dispatchEvent(new Event(REQUESTS_CHANGED_EVENT));
      return true;
    } catch (e) {
      setError(apiErrorMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = (status) => status !== req.status && run(() => employeeRequestService.updateStatus(requestId, status));
  const addFiles = (picked) => {
    const list = Array.from(picked || []);
    if (!list.length) return;
    const next = [...files];
    let err = "";
    for (const f of list) {
      if (!f.size) err = `"${f.name}" is empty`;
      else if (f.size > MAX_FILE_BYTES) err = `"${f.name}" is over ${MAX_FILE_BYTES / 1024 / 1024}MB`;
      else if (next.length >= MAX_FILES) err = `Up to ${MAX_FILES} files per message`;
      else next.push(f);
    }
    setFiles(next);
    setFileError(err);
  };
  const canSend = !!draft.trim() || files.length > 0;

  const sendReply = async () => {
    if (!canSend || busy) return;
    if (await run(() => employeeRequestService.reply(requestId, draft.trim(), files))) {
      setDraft("");
      setFiles([]);
      setFileError("");
      requestAnimationFrame(() => bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" }));
    }
  };
  const editReply = (replyId, message) => run(() => employeeRequestService.editReply(requestId, replyId, message));
  const deleteReply = (replyId) => run(() => employeeRequestService.deleteReply(requestId, replyId));
  const createTask = () => navigate("/task-board", { state: { openCreateTask: true, taskDraft: buildTaskDraft(req) } });

  const meta = req ? TYPE_META[req.type] : null;
  const TypeIcon = meta?.icon;
  const titleAr = req && isArabic(req.title);

  return (
    <>
      <div className={`er-overlay${closing ? " closing" : ""}`} onClick={close} />
      <aside className={`er-panel${closing ? " closing" : ""}`} role="dialog" aria-modal="true" aria-label="Request details">
        {meta && <div className="er-glow" style={{ background: `radial-gradient(400px 180px at 90% -40px, ${tint(meta.color, 14)}, transparent 70%)` }} />}

        <div className="er-phead">
          <div className="er-row" style={{ gap: 10 }}>
            {req && (
              <>
                <span className="er-mono" style={{ fontSize: 12, color: "var(--faint)", whiteSpace: "nowrap", flexShrink: 0 }}>{refOf(req.id)}</span>
                <span style={{ color: "var(--faint)" }}>·</span>
                <span style={{ fontSize: 12.5, color: "var(--faint)", whiteSpace: "nowrap" }}>{fullDate(req.createdAt)}</span>
              </>
            )}
            <div style={{ flex: 1 }} />
            <button type="button" className="er-btn er-iconbtn" onClick={close} title="Close" aria-label="Close panel"><X size={16} strokeWidth={2} /></button>
          </div>

          {req && (
            <>
              <div className="er-row" style={{ alignItems: "flex-start", gap: 14 }}>
                <div style={{ width: 46, height: 46, borderRadius: 14, background: tint(meta.color, 14), display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <TypeIcon size={22} strokeWidth={1.9} color={meta.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div dir="auto" className={`er-ptitle${titleAr ? " ar" : ""}`}>{req.title}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                    <span className="er-pill" style={pillStyle(meta.color)}>{typeLabel(req.type)}</span>
                    {req.severity && <span className="er-pill" style={pillStyle(SEVERITY_COLOR[req.severity])}><i />{severityLabel(req.severity)}</span>}
                    <span className="er-pill" style={pillStyle(STATUS_COLOR[req.status])}><i />{statusLabel(req.status)}</span>
                    {req.deletedByEmployee && <span className="er-pill" style={pillStyle(DELETED_COLOR)}><i />Deleted</span>}
                  </div>
                </div>
              </div>
              {canAct ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" className="er-btn er-actionbtn" onClick={() => setEmailing(true)}><Mail size={15} strokeWidth={1.9} /> Send email</button>
                  <button type="button" className="er-btn er-actionbtn primary" onClick={createTask}><Plus size={15} strokeWidth={2.2} /> Create task</button>
                </div>
              ) : (
                <span className="er-pill" style={{ background: "var(--raise)", color: "var(--muted)", justifySelf: "start", alignSelf: "flex-start" }}>View only</span>
              )}
            </>
          )}
        </div>

        <div className="er-body" ref={bodyRef}>
          {error && !req && <ErrorState message={error} />}
          {!req && !error && <div style={{ height: 160, borderRadius: 14, background: "var(--raise)", animation: "erPulse 1.6s infinite" }} />}
          {error && req && <p style={{ color: "var(--danger)", fontSize: 13 }}>{error}</p>}

          {req && (
            <>
              {req.deletedByEmployee && (
                <div role="status" style={{ borderRadius: 14, border: "1px solid color-mix(in srgb, #EF4444 45%, transparent)", background: "color-mix(in srgb, #EF4444 10%, transparent)", padding: 12, fontSize: 13, color: "var(--text)" }}>
                  <strong>Deleted by the employee</strong> · {fullDate(req.deletedAt)}
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                    It is hidden from them; everything here is kept and still visible to you.
                  </div>
                </div>
              )}

              {req.emails?.length > 0 && (
                <div role="status" style={{ borderRadius: 14, border: "1px solid var(--accent-line)", background: "var(--accent-soft)", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  {req.emails.map((m) => (
                    <div key={m.id} style={{ fontSize: 13, color: "var(--accent-ink)" }}>
                      ✉ Mail sent by <strong>{m.sentBy}</strong> · {fullDate(m.sentAt)}
                      <div style={{ fontSize: 12, opacity: 0.85, overflowWrap: "anywhere" }}>
                        “{m.subject}”{m.to && ` → ${m.to.join(", ")}`}
                        {m.cc?.length > 0 && ` · cc: ${m.cc.join(", ")}`}
                        {m.bcc?.length > 0 && ` · bcc: ${m.bcc.join(", ")}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="er-section" style={{ gap: 8 }}>
                <div className="er-label">Description</div>
                <div dir="auto" className="er-desc">{req.description}</div>
              </div>

              {req.attachments.length > 0 && <AttachmentsSection attachments={req.attachments} requestRef={refOf(req.id)} />}

              {req.tasks?.length > 0 && (
                <div className="er-section">
                  <div className="er-label">Linked tasks · {req.tasks.length}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {req.tasks.map((t) => (
                      <Link key={t.id} to={`/task-board?task=${t.id}`} className="er-row" style={{ gap: 8, fontSize: 13.5, color: "var(--accent-ink)", minWidth: 0 }}>
                        <span className="er-ellipsis" title={t.title}>#{t.number} {t.title}</span>
                        <span className="er-pill" style={{ background: "var(--raise)", color: "var(--muted)", flexShrink: 0 }}>{STATUS_LABELS[t.status] || t.status}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {canAct && (
                <div className="er-section">
                  <div className="er-label">Status</div>
                  <div className="er-statusgrid">
                    {STATUSES.map((s) => {
                      const on = req.status === s.value;
                      const c = STATUS_COLOR[s.value];
                      return (
                        <button
                          key={s.value}
                          type="button"
                          className="er-btn er-statusbtn"
                          disabled={busy}
                          aria-pressed={on}
                          onClick={() => changeStatus(s.value)}
                          style={on ? { background: tint(c, 16), borderColor: tint(c, 50), color: "var(--text)", fontWeight: 600 } : { fontWeight: 500 }}
                        >
                          <i style={{ background: c }} />{s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <EmployeeCard employee={req.employee} showFingerprint={canAct} />
              <Conversation replies={req.replies} employeeName={req.employee.name} me={me} canAct={canAct} busy={busy} onEdit={editReply} onDelete={deleteReply} />
            </>
          )}
        </div>

        {req && canAct && (
          <div
            className={`er-composer${dragging ? " dragging" : ""}`}
            onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDragging(true); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
            onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
          >
            {dragging && (
              <div className="er-dropzone"><UploadCloud size={20} /> Drop files to attach</div>
            )}
            <PendingFiles files={files} disabled={busy} onRemove={(i) => setFiles((f) => f.filter((_, k) => k !== i))} />
            {fileError && <div role="alert" className="er-fileerr">{fileError}</div>}
            <div className="er-compbox">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
              <button type="button" className="er-btn er-attach" title="Attach files" aria-label="Attach files" disabled={busy || files.length >= MAX_FILES} onClick={() => fileInputRef.current?.click()}>
                <Paperclip size={16} strokeWidth={2} />
              </button>
              <textarea
                ref={composerRef}
                dir="auto"
                rows={1}
                value={draft}
                maxLength={2000}
                onChange={(e) => setDraft(e.target.value)}
                onPaste={(e) => {
                  const pasted = Array.from(e.clipboardData?.files || []);
                  if (pasted.length) { e.preventDefault(); addFiles(pasted); }
                }}
                onKeyDown={(e) => {
                  // Enter sends, Shift+Enter adds a new line (never while an IME is composing).
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    sendReply();
                  }
                }}
                placeholder="Write a reply…"
                aria-label={`Reply to ${firstNameOf(req.employee.name)}`}
              />
              <button type="button" className={`er-btn er-send${canSend ? " ready" : ""}`} onClick={sendReply} aria-disabled={!canSend || busy}>
                Send <ArrowRight size={14} strokeWidth={2.2} />
              </button>
            </div>
          </div>
        )}
      </aside>

      {req && canAct && emailing && (
        <SendEmailModal
          requestId={requestId}
          firstName={firstNameOf(req.employee.name)}
          onClose={() => setEmailing(false)}
          onSent={fetchDetail}
        />
      )}
    </>
  );
};

export default RequestPanel;
